import { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { api, ApiError } from '@/api/client'

export function VerifyOtpPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const state = (location.state as { email?: string; password?: string; emailSent?: boolean }) || {}
  // Email comes from the sign-up navigation OR, when the user returns via the link
  // in their verification email (?email=), from the query string.
  const emailFromQuery = new URLSearchParams(location.search).get('email') || undefined
  // Trimmed: the query-string form comes back through a mail client, which can
  // append whitespace, and every downstream call keys the pending user on it.
  const email = (state.email || emailFromQuery)?.trim() || undefined
  // "Resumed" = arrived via the email link with no in-session password, so we
  // collect a password here to finish creating the account.
  const resumed = !state.password
  const [code, setCode] = useState('')
  const [password, setPassword] = useState(state.password || '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  // Sign-up now tells us whether the code actually got out. `emailSent === false`
  // means the account IS ready and the code IS stored — only delivery failed — so the
  // cure is Resend, not starting over. Undefined (a resumed user arriving via the
  // email link) is treated as "sent", because it demonstrably was.
  const [notice, setNotice] = useState(
    state.emailSent === false
      ? "Your account is ready, but we couldn't send the code. Tap Resend below."
      : '',
  )
  const [resending, setResending] = useState(false)

  const handleResend = async () => {
    if (!email || resending) return
    setResending(true)
    setError('')
    setNotice('')
    try {
      await api.post('/registration/resend', { email })
      setNotice(`We sent a new code to ${email}.`)
    } catch (err) {
      // The server answers 502 when delivery failed but the code was still stored,
      // so the user can simply try again rather than re-registering.
      setError(err instanceof Error ? err.message : "We couldn't resend the code. Please try again.")
    } finally {
      setResending(false)
    }
  }

  // Only bail out if we have no email at all — otherwise a resumed user can finish.
  useEffect(() => {
    if (!email) navigate('/account-type', { replace: true })
  }, [email, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password) return
    setLoading(true)
    setError('')
    try {
      try {
        await api.post('/registration/verify', { email, verificationCode: code })
      } catch (err) {
        // "Email already verified" is the expected answer for a RESUMED user — one
        // who verified earlier and came back via the link in their email without
        // ever setting a password. It is not a failure: the code was accepted the
        // first time, and the account still needs finishing at /registration/password.
        //
        // This page's `resumed` branch exists precisely to rescue that user, but the
        // unconditional verify below it used to abort the whole submit, so the resume
        // link could never complete a sign-up. Any OTHER error still propagates.
        //
        // COUPLING: this matches the literal the server sends — `'Email already
        // verified'` in admin-backend `routes/registration.ts` (POST /verify).
        // There is no machine-readable error code to key on. If that string is
        // reworded, reword it here too or the resume link silently breaks again.
        const alreadyVerified =
          err instanceof ApiError && err.status === 400 && /already verified/i.test(err.message)
        if (!alreadyVerified) throw err
      }
      await api.post('/registration/password', { email, verificationCode: code, password })
      window.location.href = '/portal/'
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (!email) return null

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6 py-12">
      <form onSubmit={handleSubmit} className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8 shadow-sm text-center">
        <div className="w-14 h-14 rounded-full bg-rust/10 text-rust flex items-center justify-center mx-auto mb-6">
          <Icon icon="solar:letter-linear" className="text-2xl" />
        </div>

        <h1 className="text-2xl font-medium tracking-tight text-navy mb-2">Verify Your Email</h1>
        <p className="text-sm text-slate-500 font-light mb-6">
          Enter the code we sent to <strong className="text-slate-700">{email}</strong>
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>
        )}

        {notice && !error && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
            {notice}
          </div>
        )}

        <input
          type="text"
          required
          maxLength={12}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Enter verification code"
          className="w-full px-4 py-3 border border-slate-200 rounded-lg text-center text-2xl tracking-[0.5em] font-medium focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none mb-4"
        />

        {resumed && (
          <div className="text-left mb-6">
            <label className="block text-xs text-slate-500 mb-1">Choose a password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Set your account password"
              className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              At least 8 characters, with upper &amp; lower case, a number, and a special character.
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || code.length < 4 || (resumed && password.length < 8)}
          className="w-full bg-navy text-white py-3 rounded-lg font-medium text-sm hover:bg-slate-800 transition-colors disabled:opacity-50"
        >
          {loading ? 'Setting up your account...' : 'Verify & Create Account'}
        </button>

        {/* The recovery path the server has always had and the UI never exposed: the
            pending code is stored before delivery is attempted, so a mailer outage is
            fixed by resending, not by registering again (which only mints another
            undeliverable code and then answers "User already exists"). */}
        <button
          type="button"
          onClick={handleResend}
          disabled={resending}
          className="mt-4 w-full text-sm text-slate-500 hover:text-navy transition-colors disabled:opacity-50"
        >
          {resending ? 'Sending...' : "Didn't get the code? Resend"}
        </button>

        <div className="mt-6">
          <Link to="/sign-in" className="text-sm text-slate-500 hover:text-navy transition-colors">
            Back to Sign In
          </Link>
        </div>
      </form>
    </div>
  )
}
